import {
  DataLayerContext,
  SummaryStatsType,
} from 'features/dataLayers/DataLayerProvider'
import { useCdfDataFetcher } from 'features/dataLayers/load/useCdfDataFetcher'
import { useCallback, useContext, useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import { GeckoComponentDataFrame } from 'types'
import { memoizeAsyncSuccess } from 'utils/memoizeAsyncSuccess'

import { generateQuantitativeDataStats } from './generateQuantitativeDataStats'
import { QuantitativeSummaryStatistics } from './quantitativeStatisticsDataType'

// ----------------------------------------------- Single CDF Method -----------------------------------------------

/**
 * Calculate the summary statistics for a given component data frame (mean, std dev, value range, value distribution)
 *
 * @param cdf - The component data frame to calculate summary statistics for, this variable *must* be memoized for performance
 */
export function useQuantitativeDataSummaryStats(
  cdf: GeckoComponentDataFrame | undefined
): {
  data: QuantitativeSummaryStatistics | undefined
  isLoading: boolean
  /**
   * Set when the stats could not be computed (e.g. the CDF has no parquet
   * file, so no signed URL resolves). Distinguishes a real failure from
   * "still loading" -- consumers that only watch `isLoading` cannot tell them
   * apart and will render a spinner that never goes away.
   */
  error: unknown
} {
  const quantitativeDataSummaryStatsFetcher = useQuantitativeDataStatsFetcher()
  const quantitativeDataStatsGenerator = useCallback(
    async () => ({
      stats:
        cdf !== undefined
          ? await quantitativeDataSummaryStatsFetcher(cdf)
          : undefined,
      id: cdf?.id,
      generation: cdf?.generation,
    }),
    [cdf, quantitativeDataSummaryStatsFetcher]
  )

  const { data, isLoading, error } = useSWRImmutable(
    [cdf?.id, cdf?.generation, 'stats'],
    quantitativeDataStatsGenerator,
    // There is no cheap way to compare CDF data to know if it has changed,
    // and SWR will attempt to use stableHash... which involves iterating over
    // all elements in the returned array. Very slow. For more information,
    // see: https://github.com/vercel/swr/issues/1719
    //
    // Instead, check if the cdf-id or generation has changed.
    {
      compare: (a, b) =>
        (a?.id ?? '') + (a?.generation?.toString() ?? '') ===
        (b?.id ?? '') + (b?.generation?.toString() ?? ''),
    }
  )

  return useMemo(
    () => ({
      data:
        data === undefined || data.stats === undefined || cdf === undefined
          ? undefined
          : data.stats,
      isLoading: isLoading,
      error,
    }),
    [data, isLoading, error, cdf]
  )
}

/**
 * A hook that returns a callback function that can be used to get the statistics
 * for a given cdf (for more details see generateCDFStats)
 *  Statistics about data in the given measurement units, include
 *  - maximum reading, also indicates the ceiling of the last bin
 *  - minimum reading, also indicates the floor of the first bin
 *  - mean reading
 *  - standard deviation of distribution
 *  - histogram bins where each bin is formatted as [bin index, number of readings];
 *    if finite and defined values are uniform, then histogram is undefined
 * Note that values on the boundary of bins may be assigned undeterministically
 * to the previous or following bin due to floating point error. However, this
 * should be of little concern since this function merely returns an approximation
 * of the distribution.
 * @returns     A function with a single parameter, `cdf: GeckoComponentDataFrame`,
 *              that returns the statistics for the given `cdf` (as a `Promise<CDFStatisticsData>`),
 *              or `undefined` when the CDF has no data to summarize.
 *
 *              REJECTS when the data could not be read at all (e.g. no signed
 *              URL for the parquet). Callers must handle that.
 */
export function useQuantitativeDataStatsFetcher(): (
  cdf: GeckoComponentDataFrame
) => Promise<QuantitativeSummaryStatistics | undefined> {
  const { cdfDataStatsFetchCache } = useContext(DataLayerContext)
  const cdfDataFetcher = useCdfDataFetcher()
  return useCallback(
    async (
      cdf: GeckoComponentDataFrame
    ): Promise<QuantitativeSummaryStatistics | undefined> => {
      const cachedValues = cdfDataStatsFetchCache.get(cdf.id)
      if (
        cachedValues &&
        cachedValues.generation === cdf.generation &&
        cachedValues.type === SummaryStatsType.Quantitative
      ) {
        return await cachedValues.getData()
      } else if (
        cachedValues?.generation !== cdf.generation ||
        cachedValues.type !== SummaryStatsType.Quantitative
      ) {
        cdfDataStatsFetchCache.delete(cdf.id)
      }

      async function getCDFDataFunc(): Promise<
        QuantitativeSummaryStatistics | undefined
      > {
        const data = await cdfDataFetcher(cdf)
        if (data === undefined) return undefined
        return await generateQuantitativeDataStats(data)
      }

      // Not plain `memoize`: that caches the rejected promise, so one failed
      // read would be replayed to every later consumer until a page reload.
      const memoizedGetterFn = memoizeAsyncSuccess(getCDFDataFunc)

      cdfDataStatsFetchCache.set(cdf.id, {
        generation: cdf.generation,
        getData: memoizedGetterFn,
        type: SummaryStatsType.Quantitative,
      })

      return await memoizedGetterFn()
    },
    [cdfDataFetcher, cdfDataStatsFetchCache]
  )
}
